import type { ReactNode } from "react";
import { config, useTransition } from "@react-spring/web";

import { selectIsDetailsOpen, selectSelection, useHaNView } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { ModalBackground } from "@organisms";

import { HaNErrorBoundary } from "../../shared/HaNErrorBoundary";
import { useHaNLayoutMode } from "../../state/layout/useHaNLayoutMode";
import { HaNContext } from "../HaNContext";
import { HaNDetailsPanel } from "./components/HaNDetailsPanel";

const SHOWN = { transform: "translate3d(0px, 0px, 0)", opacity: 1 };
const HIDDEN_BELOW = { transform: "translate3d(0, 100px, 0)", opacity: 0 };
const HIDDEN_LEFT = { transform: "translate3d(-100px, 0, 0)", opacity: 0 };

/**
 * The details of the tab's selection: a panel next to the sidebar on wide screens, over the sidebar on medium ones,
 * and a bottom sheet on phones. It slides in and out; opening something else while it's open swaps the content.
 */
export const HaNDetails = ({ children }: { children: ReactNode }) => {
  const { tab } = useRequiredContext(HaNContext);
  const layoutMode = useHaNLayoutMode();
  const isDetailsOpen = useHaNView(selectIsDetailsOpen(tab));
  const selection = useHaNView(selectSelection(tab));

  const shown = isDetailsOpen && selection ? { tab, selection } : null;
  const hidden = layoutMode === "mobile" ? HIDDEN_BELOW : HIDDEN_LEFT;

  // One key while open, so a new selection replaces the content instead of sliding a second panel in
  const transitions = useTransition(shown, {
    keys: details => (details ? "open" : "closed"),
    from: hidden,
    enter: SHOWN,
    leave: hidden,
    config: { ...config.stiff, clamp: true },
  });

  return (
    <div className="pointer-events-none relative z-20 col-start-1 row-[1/-1] grid lg:col-start-2">
      <HaNErrorBoundary area="Details">
        {layoutMode === "mobile" && (
          <ModalBackground
            className="pointer-events-auto"
            clickable={false}
            hideOnSmallScreen={false}
            open={!!shown}
          />
        )}
        {transitions(
          (style, details) =>
            details && (
              <HaNDetailsPanel
                style={style}
                tab={details.tab}
                selection={details.selection}
                isOpen={shown?.selection.id === details.selection.id}>
                {children}
              </HaNDetailsPanel>
            ),
        )}
      </HaNErrorBoundary>
    </div>
  );
};
